/**
 * Downscale an image file in the browser and re-encode it as JPEG.
 * Returns a new File (or the original if it is already small enough).
 */
export async function resizeImage(
  file,
  { maxDimension = 800, maxBytes = 1024 * 1024, quality = 0.85 } = {},
) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));

  if (scale === 1 && file.size <= maxBytes) {
    bitmap.close?.();
    return file;
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();

  const toBlob = (q) =>
    new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", q));

  let blob = await toBlob(quality);
  for (let q = quality - 0.15; blob && blob.size > maxBytes && q > 0.3; q -= 0.15) {
    blob = await toBlob(q);
  }
  if (!blob || blob.size > maxBytes) {
    throw new Error("Image is too large even after compression.");
  }

  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}
